import _ from 'lodash'
import * as regression from 'd3-regression'
import * as scale from 'd3-scale'
import * as array from 'd3-array'
import applyFormatter from './apply-formatter'

// The chart code will use a linear scale to graph these regressions.
// Use an index based approach, but don't start at 0. An x value of 0 causes issues with things like log.
const x = (d, i) => i + 1
const y = d => d.raw
const linear = regression.regressionLinear().x(x).y(y)

/**
 *
 * @param prepared The prepared chart data object, may have been partially
 *     prepared.
 * @param chart
 * @param chartData
 */
const prepareTrendLines = ({prepared, chart, chartData}) => {

	/*  Notes from https://github.com/SpiderStrategies/Scoreboard/issues/36719

	If they’re “repeating” by a date/time field and “repeating again”
	by a non-date/time field, we’ll draw trend lines at the “repeating” level.
	That means there will be multiple short trend lines, one for every “repeating again” block.

	If they’re “repeating” by a non-date/time field and “repeating again”
	by a date/time field, we’ll draw a single trend line with points at
	every “repeating again” value.

	If they’re both “repeating” and “repeating again” by date/time fields,
	that’s really an edge case. Let’s just draw the trend line at the “repeating”
	scope so there will be multiple short trend lines.

	*/

	if (_.result(chart, 'breakdowns.toArray[0].isCalendarPeriodBreakdown')) {
		_prepareLinePerPlot(prepared, chartData)
	} else {
		_(prepared.plots)
			.flatMap(p => p.series)
			.filter({type: 'regression'})
			.groupBy('underlyingSerieId')
			.forEach((seriePlotPoints) => _prepareLineAcrossAllPlots(seriePlotPoints))
	}
}

function _prepareLinePerPlot(prepared, chartData) {
	_.each(prepared.plots, plot => {
		_(plot.series)
			.filter({type: 'regression'})
			.forEach(serie => {
				let range = plot.breakdown.range
				let x = (d, i) => {
					let idx = range.findIndex(range => range.id == d.rangeId)
					return idx + 1
				}
				let y = d => d.raw
				let linear = regression.regressionLinear().x(x).y(y)
				let regressionLinear = linear(serie.points)

				serie.prediction = regressionLinear.predict

				// #52629 The trend line needs to extend past the beginning and
				// end of the plotted points, so start at x=0 (the first plotted
				// point is x=1) and go one past range.length (the last plotted
				// point)
				let point0 = [0, regressionLinear.predict(0)]
				let point1 = [range.length + 1, regressionLinear.predict(range.length + 1)]
				serie.points = [point0, point1]

				_assignPredictions(serie, plot.breakdown.range.length + 1)
				applyFormatter(serie, chartData)
			})
	})
}

function _prepareLineAcrossAllPlots(seriePlots) {

	const regressionData = seriePlots.map(spp => spp.points).flat()
	// Linear regression based on all the points in all plots
	const regressionLinear = linear(regressionData)

	const scaler = scale.scaleBand()
		.domain(array.range(seriePlots.length))
		.range([regressionLinear[0][0], regressionLinear[1][0]])

	seriePlots.forEach((plotSerie, plotIdx) => {
		let startPoint = scaler(plotIdx)
		let endPoint = scaler(plotIdx + 1)

		if (endPoint === undefined) {
			endPoint = scaler(plotIdx) + scaler.bandwidth()
		}

		const numPoints = _.size(plotSerie.points)
		plotSerie.prediction = regressionLinear.predict
		// #52629 The trend line needs to extend past the beginning and
		// end of the plotted points, so start at x=0 (the first plotted
		// point is x=1) and go one past range.length (the last plotted
		// point)
		plotSerie.points = [
			[startPoint, regressionLinear.predict(startPoint - 1)],
			[endPoint, regressionLinear.predict(endPoint + 1)]
		]
		_assignPredictions(plotSerie, numPoints)
	})

}

function _assignPredictions(serie, pointCount) {
	serie.predictedValues = []
	_.times(pointCount, i => serie.predictedValues.push(serie.prediction(i)))
}

export default prepareTrendLines
prepareTrendLines.__filename__ = 'charts/chart-data/prepare-trend-lines.js'
